import { useEffect, useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import {
  Search,
  Bell,
  ChevronsRight,
  Menu as MenuIcon,
  User,
  LogOut,
  Sparkles,
} from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { IconButton } from "@/components/IconButton";
import { Menu } from "@/components/Menu";
import { ThemePicker } from "@/shell/ThemePicker";

interface SharedProps {
  currentUser?: { name: string; email: string; avatarUrl?: string | null } | null;
}

const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

// Sign out with a real full-page DELETE so the server redirect to the root
// view is followed by the browser (not as an Inertia XHR — root renders the
// non-Inertia public page when logged out).
function signOut() {
  const form = document.createElement("form");
  form.method = "post";
  form.action = "/logout";
  form.style.display = "none";

  const method = document.createElement("input");
  method.type = "hidden";
  method.name = "_method";
  method.value = "delete";
  form.appendChild(method);

  const token = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content");
  if (token) {
    const csrf = document.createElement("input");
    csrf.type = "hidden";
    csrf.name = "authenticity_token";
    csrf.value = token;
    form.appendChild(csrf);
  }

  document.body.appendChild(form);
  form.submit();
}

interface PortalTopBarProps {
  sidebarCollapsed: boolean;
  onExpandSidebar: () => void;
  onOpenMobileMenu: () => void;
}

export function PortalTopBar({
  sidebarCollapsed,
  onExpandSidebar,
  onOpenMobileMenu,
}: PortalTopBarProps) {
  const { currentUser } = usePage().props as unknown as SharedProps;
  const me = {
    name: currentUser?.name || "Account",
    email: currentUser?.email || "",
    avatarUrl: currentUser?.avatarUrl || null,
  };
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="flex h-14 items-center gap-2 border-b border-border-subtle bg-canvas px-3 sm:gap-3 sm:px-4">
      <IconButton
        aria-label="Open menu"
        onClick={onOpenMobileMenu}
        className="md:hidden"
      >
        <MenuIcon />
      </IconButton>

      {sidebarCollapsed && (
        <IconButton
          aria-label="Expand sidebar"
          onClick={onExpandSidebar}
          className="hidden md:inline-flex"
        >
          <ChevronsRight />
        </IconButton>
      )}

      <IconButton
        aria-label="Open search"
        onClick={() => setPaletteOpen(true)}
        className="sm:hidden"
      >
        <Search />
      </IconButton>
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="hidden sm:flex group h-8 w-64 items-center gap-2 rounded-[var(--radius-md)] border border-border-default bg-surface px-2.5 text-sm text-fg-subtle hover:border-border-strong hover:bg-surface-hover transition-colors"
      >
        <Search className="size-4" />
        <span>Search…</span>
        <kbd className="ml-auto rounded-[var(--radius-sm)] border border-border-subtle bg-muted px-1.5 py-0.5 font-mono text-[10px]">
          {isMac ? "⌘" : "Ctrl"}K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-1">
        <ThemePicker />
        <IconButton aria-label="Notifications" className="hidden sm:inline-flex">
          <Bell />
        </IconButton>
        <Menu.Root>
          <Menu.Trigger asChild>
            <button
              type="button"
              className="ml-1 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
              aria-label="Account menu"
            >
              <Avatar name={me.name} src={me.avatarUrl} size="md" />
            </button>
          </Menu.Trigger>
          <Menu.Content align="end" sideOffset={8} className="min-w-[14rem]">
            <div className="flex items-center gap-2 px-2 py-2">
              <Avatar name={me.name} src={me.avatarUrl} size="md" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-fg">{me.name}</div>
                {me.email && (
                  <div className="truncate text-xs text-fg-muted">{me.email}</div>
                )}
              </div>
            </div>
            <Menu.Separator />
            <Menu.Item asChild>
              <Link href="/account">
                <User /> Account
              </Link>
            </Menu.Item>
            <Menu.Separator />
            <Menu.Item destructive onSelect={signOut}>
              <LogOut /> Sign out
            </Menu.Item>
          </Menu.Content>
        </Menu.Root>
      </div>

      <CommandPaletteStub open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </header>
  );
}

/* Lightweight search stub for now — full portal-tuned palette ships in a later slice. */
function CommandPaletteStub({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-overlay backdrop-blur-[2px]"
      />
      <div className="relative w-full max-w-xl rounded-[var(--radius-lg)] border border-border-subtle bg-surface p-4 shadow-[var(--shadow-4)]">
        <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
          <Sparkles className="size-4 text-accent-text" />
          <input
            autoFocus
            type="text"
            placeholder="Search contributions, projects, goals…"
            className="flex-1 bg-transparent text-sm text-fg placeholder:text-fg-subtle outline-none"
          />
          <kbd className="rounded-[var(--radius-sm)] border border-border-subtle bg-muted px-1.5 py-0.5 font-mono text-[10px] text-fg-muted">
            esc
          </kbd>
        </div>
        <p className="mt-4 text-xs text-fg-subtle">
          <span className="text-fg-disabled">Full search coming in a later slice.</span>
        </p>
      </div>
    </div>
  );
}
